import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const Venues: React.FC = () => {
  const [venues, setVenues] = useState<any[]>([]);
  const [newVenue, setNewVenue] = useState({ name: '', address: '', contactName: '', phoneNumber: '', pastEventsBase: '0', notes: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const navigate = useNavigate();

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found. Please log in again.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Venues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVenues(response.data);
    } catch (error: any) {
      console.error('Failed to fetch venues:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError(`Error: ${error.message}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateVenue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVenue.name || !newVenue.address) {
      setError('Name and Address are required.');
      return;
    }
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found. Please log in again.');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/Venues`,
        {
          name: newVenue.name,
          address: newVenue.address,
          contactName: newVenue.contactName,
          phoneNumber: newVenue.phoneNumber,
          pastEventsBase: parseInt(newVenue.pastEventsBase) || 0,
          notes: newVenue.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewVenue({ name: '', address: '', contactName: '', phoneNumber: '', pastEventsBase: '0', notes: '' });
      setSuccess('Venue created successfully!');
      setShowModal(false); // Close the modal on success
      fetchVenues();
    } catch (error: any) {
      console.error('Failed to create venue:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError(`Error: ${error.message}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteVenue = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this venue? This action cannot be undone.')) return;
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found. Please log in again.');
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/Venues/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVenues(venues.filter(venue => venue.id !== id));
      setSuccess('Venue deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete venue:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else if (error.response && error.response.status === 400) {
        setError('Cannot delete venue with associated clients. Remove venue from clients first.');
      } else {
        setError(`Error: ${error.message}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: string) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortField(field);
    setSortDirection(isAsc ? 'desc' : 'asc');

    const sortedVenues = [...venues].sort((a, b) => {
      let valueA = a[field];
      let valueB = b[field];

      if (field === 'pastEvents' || field === 'upcomingEvents' || field === 'pastEventsBase') {
        valueA = parseInt(valueA) || 0;
        valueB = parseInt(valueB) || 0;
      } else {
        valueA = valueA ? valueA.toString().toLowerCase() : '';
        valueB = valueB ? valueB.toString().toLowerCase() : '';
      }

      if (valueA < valueB) return isAsc ? 1 : -1;
      if (valueA > valueB) return isAsc ? -1 : 1;
      return 0;
    });

    setVenues(sortedVenues);
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Venues</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {isLoading && <div className="alert alert-info">Loading...</div>}

      {/* Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Venue</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} disabled={isLoading}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleCreateVenue}>
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className={`form-control ${!newVenue.name && error ? 'is-invalid' : ''}`}
                      placeholder="Enter name"
                      value={newVenue.name}
                      onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      className={`form-control ${!newVenue.address && error ? 'is-invalid' : ''}`}
                      placeholder="Enter address"
                      value={newVenue.address}
                      onChange={(e) => setNewVenue({ ...newVenue, address: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Contact Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter contact (optional)"
                      value={newVenue.contactName}
                      onChange={(e) => setNewVenue({ ...newVenue, contactName: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter phone (optional)"
                      value={newVenue.phoneNumber}
                      onChange={(e) => setNewVenue({ ...newVenue, phoneNumber: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Past Events Base</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newVenue.pastEventsBase}
                      onChange={(e) => setNewVenue({ ...newVenue, pastEventsBase: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter notes (optional)"
                      value={newVenue.notes}
                      onChange={(e) => setNewVenue({ ...newVenue, notes: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                    {isLoading ? 'Adding...' : 'Add Venue'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Venue List ({venues.length})</h4>
          <button
            className="btn btn-light text-dark"
            onClick={() => setShowModal(true)}
            disabled={isLoading}
          >
            Add Venue
          </button>
        </div>
        <div className="card-body">
          {!isLoading && venues.length === 0 ? (
            <p className="text-muted text-center">No venues found.</p>
          ) : (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    Name {getSortIcon('name')}
                  </th>
                  <th onClick={() => handleSort('address')} style={{ cursor: 'pointer' }}>
                    Address {getSortIcon('address')}
                  </th>
                  <th onClick={() => handleSort('contactName')} style={{ cursor: 'pointer' }}>
                    Contact Name {getSortIcon('contactName')}
                  </th>
                  <th onClick={() => handleSort('phoneNumber')} style={{ cursor: 'pointer' }}>
                    Phone Number {getSortIcon('phoneNumber')}
                  </th>
                  <th onClick={() => handleSort('pastEventsBase')} style={{ cursor: 'pointer' }}>
                    Past Events Base {getSortIcon('pastEventsBase')}
                  </th>
                  <th onClick={() => handleSort('pastEvents')} style={{ cursor: 'pointer' }}>
                    Total Past Events {getSortIcon('pastEvents')}
                  </th>
                  <th onClick={() => handleSort('upcomingEvents')} style={{ cursor: 'pointer' }}>
                    Upcoming Events {getSortIcon('upcomingEvents')}
                  </th>
                  <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                    Notes {getSortIcon('notes')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {venues.map(venue => (
                  <tr key={venue.id}>
                    <td>{venue.name}</td>
                    <td>{venue.address}</td>
                    <td>{venue.contactName || 'N/A'}</td>
                    <td>{venue.phoneNumber || 'N/A'}</td>
                    <td>{venue.pastEventsBase}</td>
                    <td>{venue.pastEvents}</td>
                    <td>{venue.upcomingEvents}</td>
                    <td>{venue.notes || 'N/A'}</td>
                    <td>
                      <Link to={`/venues/${venue.id}`} className="btn btn-sm btn-outline-primary me-2">View</Link>
                      <button
                        onClick={() => deleteVenue(venue.id)}
                        className="btn btn-sm btn-danger"
                        disabled={isLoading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Venues;